import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { api } from '../hooks/useApi';
import { useTheme } from '../context/ThemeContext';
import useMobile from '../hooks/useMobile';

function fmtBytes(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)    return 'nettopp';
  if (diff < 3600)  return `${Math.floor(diff / 60)} min siden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}t siden`;
  return new Date(dateStr).toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Lagringsindikator ────────────────────────────────────────────────────
function StorageBar({ storage }) {
  if (!storage) return null;
  const pct = storage.percent_used || 0;
  const isFull = pct >= 100;
  const isWarn = pct >= 80;
  const barColor = isFull ? '#ef4444' : isWarn ? '#f59e0b' : '#7C6FF7';
  return (
    <div style={S.storageWrap}>
      <div style={S.storageTop}>
        <span style={S.storageLabel}>💾 {storage.used_gb} GB brukt av {storage.limit_gb} GB</span>
        <span style={{ ...S.storagePct, color: barColor }}>{pct}%</span>
      </div>
      <div style={S.storageTrack}>
        <div style={{ ...S.storageBar, width: `${Math.min(pct, 100)}%`, background: barColor }} />
      </div>
      {isFull && (
        <div style={S.premiumBanner}>
          🚀 Lagringsgrensen er nådd — <strong>Oppgrader til Premium</strong> for 10 GB
        </div>
      )}
      {isWarn && !isFull && (
        <div style={S.warnBanner}>⚠️ Over 80% av lagringen er brukt</div>
      )}
    </div>
  );
}

// ─── Lightbox ─────────────────────────────────────────────────────────────
function Lightbox({ photos, index, onClose, onDelete }) {
  const [current, setCurrent] = useState(index);
  const photo = photos[current];

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape')      onClose();
      if (e.key === 'ArrowRight')  setCurrent(c => Math.min(c + 1, photos.length - 1));
      if (e.key === 'ArrowLeft')   setCurrent(c => Math.max(c - 1, 0));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [photos.length, onClose]);

  if (!photo) return null;

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = photo.url;
    a.download = `foto-${photo.id}.jpg`;
    a.target = '_blank';
    a.click();
  };

  const handleShare = () => {
    navigator.clipboard.writeText(photo.url);
    alert('Bildelenke kopiert til utklippstavlen!');
  };

  return (
    <div style={S.lightboxOverlay} onClick={onClose}>
      <div style={S.lightboxContent} onClick={e => e.stopPropagation()}>
        <button style={S.lbClose} onClick={onClose}>✕</button>
        {current > 0 && (
          <button style={{ ...S.lbArrow, left: 12 }} onClick={() => setCurrent(c => c - 1)}>‹</button>
        )}
        {current < photos.length - 1 && (
          <button style={{ ...S.lbArrow, right: 12 }} onClick={() => setCurrent(c => c + 1)}>›</button>
        )}
        <img src={photo.url} alt={photo.caption || ''} style={S.lbImg} />
        <div style={S.lbMeta}>
          <div style={S.lbMetaLeft}>
            {photo.caption && <p style={S.lbCaption}>{photo.caption}</p>}
            <p style={S.lbInfo}>
              Lastet opp av <strong>{photo.uploaded_by_name}</strong> · {timeAgo(photo.created_at)}
              {photo.size_bytes ? ` · ${fmtBytes(photo.size_bytes)}` : ''}
            </p>
          </div>
          <div style={S.lbActions}>
            <button style={S.lbBtn} onClick={handleDownload} title="Last ned">⬇</button>
            <button style={S.lbBtn} onClick={handleShare} title="Kopier lenke">🔗</button>
            <button style={{ ...S.lbBtn, color: '#ef4444' }}
              onClick={() => { onDelete(photo.id); onClose(); }} title="Slett">🗑</button>
          </div>
        </div>
        <div style={S.lbCounter}>{current + 1} / {photos.length}</div>
      </div>
    </div>
  );
}

// ─── Opplastingszone ──────────────────────────────────────────────────────
function UploadZone({ albumId, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]   = useState([]);

  const onDrop = useCallback(async (files) => {
    setUploading(true);
    setProgress(files.map(f => ({ name: f.name, status: 'venter' })));

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setProgress(p => p.map((x, j) => j === i ? { ...x, status: 'laster opp…' } : x));
      const form = new FormData();
      form.append('photo', file);
      try {
        await axios.post(`/api/photos/albums/${albumId}/photos`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setProgress(p => p.map((x, j) => j === i ? { ...x, status: '✅' } : x));
      } catch (e) {
        const msg = e.response?.data?.error || e.message || 'feil';
        setProgress(p => p.map((x, j) => j === i ? { ...x, status: `❌ ${msg}` } : x));
      }
    }

    setUploading(false);
    onUploaded();
    setTimeout(() => setProgress([]), 3000);
  }, [albumId, onUploaded]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic'] },
    multiple: true,
    disabled: uploading,
  });

  return (
    <div>
      <div {...getRootProps()} style={{
        ...S.dropzone,
        borderColor: isDragActive ? '#7C6FF7' : '#3a3a5c',
        background:  isDragActive ? 'rgba(124,111,247,0.1)' : 'rgba(255,255,255,0.03)',
      }}>
        <input {...getInputProps()} />
        {isDragActive
          ? <p style={S.dropText}>Slipp bildene her …</p>
          : <p style={S.dropText}>📸 Dra bilder hit, eller klikk for å velge</p>
        }
        <p style={S.dropSub}>JPG, PNG, GIF, WebP, HEIC — maks 20 MB per bilde</p>
      </div>
      {progress.length > 0 && (
        <div style={S.progressList}>
          {progress.map((p, i) => (
            <div key={i} style={S.progressItem}>
              <span style={S.progressName}>{p.name}</span>
              <span style={S.progressStatus}>{p.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Masonry-grid ─────────────────────────────────────────────────────────
function PhotoGrid({ photos, onPhotoClick }) {
  if (photos.length === 0)
    return <p style={S.emptyText}>Ingen bilder her ennå. Last opp det første!</p>;

  return (
    <div style={S.masonry}>
      {photos.map((photo, idx) => (
        <div key={photo.id} style={S.masonryItem}
          onClick={() => onPhotoClick(idx)}
          onMouseEnter={e => {
            e.currentTarget.querySelector('img').style.transform = 'scale(1.03)';
            e.currentTarget.querySelector('.overlay').style.opacity = '1';
          }}
          onMouseLeave={e => {
            e.currentTarget.querySelector('img').style.transform = 'scale(1)';
            e.currentTarget.querySelector('.overlay').style.opacity = '0';
          }}
        >
          <img src={photo.thumbnail_url || photo.url} alt={photo.caption || ''}
            style={S.masonryImg} loading="lazy" />
          <div className="overlay" style={S.masonryOverlay}>
            {photo.caption && <p style={S.masonryCaption}>{photo.caption}</p>}
            <p style={S.masonryMeta}>{photo.uploaded_by_name} · {timeAgo(photo.created_at)}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Albumkort ────────────────────────────────────────────────────────────
function AlbumCard({ album, onClick, onDelete }) {
  const hasCover = !!album.cover_url;
  const cardStyle = {
    ...S.albumCard,
    ...(hasCover
      ? { backgroundImage: `url(${album.cover_url})`, backgroundSize: 'cover', backgroundPosition: 'center', background: undefined }
      : { background: 'linear-gradient(135deg, rgba(124,111,247,0.18) 0%, rgba(247,96,92,0.10) 100%)' }
    ),
  };

  return (
    <div style={cardStyle} onClick={onClick}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.35)';
        const btn = e.currentTarget.querySelector('.del-btn');
        if (btn) btn.style.opacity = '1';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = '';
        const btn = e.currentTarget.querySelector('.del-btn');
        if (btn) btn.style.opacity = '0';
      }}
    >
      {hasCover && <div style={S.albumOverlay} />}
      <div style={S.albumContent}>
        <span style={S.albumCountBadge}>{album.photo_count || 0} bilder</span>
        <h3 style={{ ...S.albumName, ...(hasCover ? S.albumNameLight : S.albumNameDark) }}>
          {album.name}
        </h3>
        {album.description && (
          <p style={{ ...S.albumDesc, ...(hasCover ? { color: 'rgba(255,255,255,0.8)' } : {}) }}>
            {album.description}
          </p>
        )}
      </div>
      <button
        className="del-btn"
        style={S.albumDeleteBtn}
        onClick={e => { e.stopPropagation(); onDelete(album.id, album.name); }}
        title="Slett album"
      >🗑</button>
    </div>
  );
}

// ─── Hoved-komponent ──────────────────────────────────────────────────────
export default function PhotoPage() {
  const { dark } = useTheme();
  const isMobile = useMobile();
  const [albums,       setAlbums]       = useState([]);
  const [activeAlbum,  setActiveAlbum]  = useState(null);
  const [photos,       setPhotos]       = useState([]);
  const [storage,      setStorage]      = useState(null);
  const [lightboxIdx,  setLightboxIdx]  = useState(null);
  const [showNewAlbum, setShowNewAlbum] = useState(false);
  const [newAlbumName, setNewAlbumName] = useState('');
  const [newAlbumDesc, setNewAlbumDesc] = useState('');
  const [loading,      setLoading]      = useState(true);

  const loadAlbums  = useCallback(() => api.get('/api/photos/albums').then(setAlbums).catch(console.error), []);
  const loadStorage = useCallback(() => api.get('/api/photos/storage').then(setStorage).catch(console.error), []);
  const loadPhotos  = useCallback((albumId) => api.get(`/api/photos/albums/${albumId}/photos`).then(setPhotos).catch(console.error), []);

  useEffect(() => {
    Promise.all([loadAlbums(), loadStorage()]).finally(() => setLoading(false));
  }, [loadAlbums, loadStorage]);

  const openAlbum = (album) => {
    setActiveAlbum(album);
    loadPhotos(album.id);
  };

  const closeAlbum = () => {
    setActiveAlbum(null);
    setPhotos([]);
  };

  const createAlbum = async () => {
    if (!newAlbumName.trim()) return;
    try {
      await api.post('/api/photos/albums', { name: newAlbumName.trim(), description: newAlbumDesc.trim() || undefined });
      setNewAlbumName('');
      setNewAlbumDesc('');
      setShowNewAlbum(false);
      loadAlbums();
    } catch (e) {
      alert('Kunne ikke opprette album: ' + (e.response?.data?.error || e.message));
    }
  };

  const deleteAlbum = async (albumId, albumName) => {
    if (!window.confirm(`Slett albumet "${albumName}" og alle bildene i det?`)) return;
    try {
      await api.delete(`/api/photos/albums/${albumId}`);
      loadAlbums();
      loadStorage();
    } catch (e) {
      alert('Feil ved sletting: ' + (e.response?.data?.error || e.message));
    }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm('Slett dette bildet?')) return;
    try {
      await api.delete(`/api/photos/photos/${photoId}`);
      if (activeAlbum) loadPhotos(activeAlbum.id);
      loadStorage();
    } catch (e) {
      alert('Feil ved sletting: ' + (e.response?.data?.error || e.message));
    }
  };

  const pageBg = dark ? '#0A0818' : '#EDE8FF';
  const txt    = dark ? '#E8E8F8' : '#1A1A35';
  const sub    = dark ? '#9090B8' : '#888';

  // ── Mobile layout ──────────────────────────────────────────────────────
  if (isMobile) {
    if (activeAlbum) {
      return (
        <div style={{ background:pageBg, minHeight:'100%', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
          {/* Album hero header */}
          <div style={{
            background: dark
              ? 'linear-gradient(145deg, #2D1B69 0%, #1A1245 55%, #0D0B1E 100%)'
              : 'linear-gradient(145deg, #7C6FF7 0%, #9358E8 45%, #5E4AE3 100%)',
            padding: '52px 20px 20px',
            position: 'relative',
            overflow: 'hidden',
          }}>
            <div style={{ position:'absolute', top:-40, right:-40, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }}/>
            <div style={{ position:'relative', zIndex:1, display:'flex', alignItems:'center', gap:14 }}>
              <button onClick={closeAlbum} style={{ background:'rgba(255,255,255,0.15)', border:'none', borderRadius:10, color:'#fff', padding:'8px 12px', fontSize:16, cursor:'pointer', fontWeight:700 }}>←</button>
              <div>
                <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', fontWeight:500 }}>Famora</div>
                <div style={{ fontSize:20, fontWeight:800, color:'#fff' }}>{activeAlbum.name}</div>
              </div>
            </div>
          </div>
          <div style={{ padding:'12px 16px 80px' }}>
            <StorageBar storage={storage} />
            <UploadZone albumId={activeAlbum.id} onUploaded={() => { loadPhotos(activeAlbum.id); loadStorage(); loadAlbums(); }} />
            {lightboxIdx !== null && (
              <Lightbox photos={photos} index={lightboxIdx} onClose={() => setLightboxIdx(null)} onDelete={deletePhoto} />
            )}
            <PhotoGrid photos={photos} onPhotoClick={setLightboxIdx} />
          </div>
        </div>
      );
    }
    return (
      <div style={{ background:pageBg, minHeight:'100%', fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
        {/* Gradient Hero Header */}
        <div style={{
          background: dark
            ? 'linear-gradient(145deg, #2D1B69 0%, #1A1245 55%, #0D0B1E 100%)'
            : 'linear-gradient(145deg, #7C6FF7 0%, #9358E8 45%, #5E4AE3 100%)',
          padding: '52px 20px 20px',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <div style={{ position:'absolute', top:-40, right:-40, width:160, height:160, borderRadius:'50%', background:'rgba(255,255,255,0.06)', pointerEvents:'none' }}/>
          <div style={{ position:'absolute', bottom:-60, left:-20, width:140, height:140, borderRadius:'50%', background:'rgba(255,255,255,0.04)', pointerEvents:'none' }}/>
          <div style={{ position:'relative', zIndex:1, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div>
              <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', fontWeight:500, marginBottom:2 }}>Famora</div>
              <div style={{ fontSize:24, fontWeight:800, color:'#fff' }}>Bilder</div>
              <div style={{ fontSize:13, color:'rgba(255,255,255,0.65)', marginTop:2 }}>{albums.length} album</div>
            </div>
            <button onClick={() => setShowNewAlbum(v => !v)} style={{ background:'rgba(255,255,255,0.15)', backdropFilter:'blur(10px)', color:'#fff', border:'1px solid rgba(255,255,255,0.25)', borderRadius:12, padding:'8px 16px', fontWeight:700, fontSize:14, cursor:'pointer' }}>
              {showNewAlbum ? 'Avbryt' : '+ Nytt album'}
            </button>
          </div>
        </div>

        <div style={{ padding:'12px 16px 80px' }}>
          {showNewAlbum && (
            <div className="glass-card" style={{ marginBottom:16, padding:14, display:'flex', flexDirection:'column', gap:10 }}>
              <input style={{ width:'100%', padding:'12px 14px', borderRadius:12, border:'1px solid '+(dark?'rgba(255,255,255,0.12)':'rgba(124,111,247,0.2)'), background: dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.7)', color:txt, fontSize:14, fontFamily:'inherit', outline:'none', boxSizing:'border-box' }}
                placeholder="Albumets navn" value={newAlbumName} onChange={e => setNewAlbumName(e.target.value)} autoFocus/>
              <input style={{ width:'100%', padding:'12px 14px', borderRadius:12, border:'1px solid '+(dark?'rgba(255,255,255,0.12)':'rgba(124,111,247,0.2)'), background: dark?'rgba(255,255,255,0.08)':'rgba(255,255,255,0.7)', color:txt, fontSize:14, fontFamily:'inherit', outline:'none', boxSizing:'border-box' }}
                placeholder="Beskrivelse (valgfritt)" value={newAlbumDesc} onChange={e => setNewAlbumDesc(e.target.value)}/>
              <button onClick={createAlbum} style={{ background:'linear-gradient(135deg,#7C6FF7,#9358E8)', color:'#fff', border:'none', borderRadius:12, padding:13, fontWeight:700, fontSize:15, cursor:'pointer' }}>Opprett album</button>
            </div>
          )}

          <StorageBar storage={storage} />

          {loading && <p style={{ color:sub, textAlign:'center', padding:'20px 0' }}>Laster …</p>}

          {!loading && albums.length === 0 && (
            <div style={{ textAlign:'center', padding:'60px 20px', color:sub }}>
              <div style={{ fontSize:56, marginBottom:12 }}>📷</div>
              <div style={{ fontSize:16, fontWeight:700, color:txt }}>Ingen album ennå</div>
              <div style={{ fontSize:14, marginTop:6 }}>Lag ditt første album</div>
            </div>
          )}

          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {albums.map(album => (
              <div key={album.id} className="glass-card" style={{ padding:0, overflow:'hidden', cursor:'pointer' }} onClick={() => openAlbum(album)}>
                {album.cover_url ? (
                  <div style={{ height:140, backgroundImage:`url(${album.cover_url})`, backgroundSize:'cover', backgroundPosition:'center', position:'relative' }}>
                    <div style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.35)' }}/>
                    <div style={{ position:'absolute', bottom:12, left:14, right:14 }}>
                      <div style={{ fontSize:16, fontWeight:800, color:'#fff' }}>{album.name}</div>
                      {album.description && <div style={{ fontSize:12, color:'rgba(255,255,255,0.8)', marginTop:2 }}>{album.description}</div>}
                    </div>
                    <div style={{ position:'absolute', top:10, right:10, background:'rgba(0,0,0,0.4)', borderRadius:20, padding:'3px 10px', fontSize:11, color:'#fff', fontWeight:700 }}>{album.photo_count||0} bilder</div>
                  </div>
                ) : (
                  <div style={{ padding:'16px', display:'flex', alignItems:'center', gap:14 }}>
                    <div style={{ width:52, height:52, borderRadius:12, background:'linear-gradient(135deg,rgba(124,111,247,0.3),rgba(147,88,232,0.2))', display:'flex', alignItems:'center', justifyContent:'center', fontSize:24, flexShrink:0 }}>📸</div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:16, fontWeight:700, color:txt }}>{album.name}</div>
                      {album.description && <div style={{ fontSize:12, color:sub, marginTop:2 }}>{album.description}</div>}
                      <div style={{ fontSize:12, color:'#7C6FF7', marginTop:4, fontWeight:600 }}>{album.photo_count||0} bilder</div>
                    </div>
                    <span style={{ color:sub, fontSize:16 }}>›</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── Album-innhold (desktop) ──────────────────────────────────────────────
  if (activeAlbum) {
    return (
      <div style={S.page}>
        <div style={S.header}>
          <button style={S.backBtn} onClick={closeAlbum}>← Tilbake</button>
          <h1 style={S.title}>{activeAlbum.name}</h1>
        </div>

        <StorageBar storage={storage} />

        <UploadZone
          albumId={activeAlbum.id}
          onUploaded={() => { loadPhotos(activeAlbum.id); loadStorage(); loadAlbums(); }}
        />

        {lightboxIdx !== null && (
          <Lightbox
            photos={photos}
            index={lightboxIdx}
            onClose={() => setLightboxIdx(null)}
            onDelete={deletePhoto}
          />
        )}

        <PhotoGrid photos={photos} onPhotoClick={setLightboxIdx} />
      </div>
    );
  }

  // ── Album-oversikt ─────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <div style={S.header}>
        <h1 style={S.title}>📸 Fotoalbum</h1>
        <button style={S.primaryBtn} onClick={() => setShowNewAlbum(true)}>+ Nytt album</button>
      </div>

      <StorageBar storage={storage} />

      {showNewAlbum && (
        <div style={S.newAlbumForm}>
          <input
            style={S.input}
            placeholder="Albumets navn"
            value={newAlbumName}
            onChange={e => setNewAlbumName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && createAlbum()}
            autoFocus
          />
          <input
            style={S.input}
            placeholder="Beskrivelse (valgfritt)"
            value={newAlbumDesc}
            onChange={e => setNewAlbumDesc(e.target.value)}
          />
          <div style={S.newAlbumBtns}>
            <button style={S.primaryBtn} onClick={createAlbum}>Opprett</button>
            <button style={S.ghostBtn} onClick={() => { setShowNewAlbum(false); setNewAlbumName(''); setNewAlbumDesc(''); }}>
              Avbryt
            </button>
          </div>
        </div>
      )}

      {loading && <p style={S.emptyText}>Laster …</p>}

      {!loading && albums.length === 0 && (
        <div style={S.emptyState}>
          <p style={S.emptyEmoji}>📷</p>
          <p style={S.emptyText}>Ingen album ennå. Lag ditt første!</p>
        </div>
      )}

      <div style={S.albumGrid}>
        {albums.map(album => (
          <AlbumCard
            key={album.id}
            album={album}
            onClick={() => openAlbum(album)}
            onDelete={deleteAlbum}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Stiler ───────────────────────────────────────────────────────────────
const S = {
  page: { padding: '20px', maxWidth: 1100, margin: '0 auto', color: 'var(--txt)' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, gap: 12 },
  title:  { margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--txt)', flex: 1 },
  backBtn: { background: 'none', border: 'none', color: 'var(--accent)', fontSize: 16, cursor: 'pointer', padding: '6px 0' },
  primaryBtn: { background: '#7C6FF7', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 14, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' },
  ghostBtn:   { background: 'transparent', color: 'var(--txm)', border: '1px solid var(--bdr)', borderRadius: 8, padding: '8px 16px', fontSize: 14, cursor: 'pointer' },

  storageWrap: { background: 'var(--card)', border: '1px solid var(--bdr)', borderRadius: 10, padding: '12px 16px', marginBottom: 20 },
  storageTop:  { display: 'flex', justifyContent: 'space-between', marginBottom: 6 },
  storageLabel: { fontSize: 13, color: 'var(--txm)' },
  storagePct:   { fontSize: 13, fontWeight: 700 },
  storageTrack: { height: 6, borderRadius: 99, background: 'var(--bdr)', overflow: 'hidden' },
  storageBar:   { height: '100%', borderRadius: 99, transition: 'width 0.4s ease' },
  premiumBanner: { marginTop: 10, padding: '8px 12px', background: 'rgba(124,111,247,0.12)', border: '1px solid var(--accent)', borderRadius: 8, fontSize: 13, color: 'var(--accent)' },
  warnBanner:    { marginTop: 10, padding: '8px 12px', background: 'var(--yellow-bg)', border: '1px solid var(--yellow)', borderRadius: 8, fontSize: 13, color: 'var(--yellow)' },

  newAlbumForm: { background: 'var(--card)', border: '1px solid var(--bdr)', borderRadius: 12, padding: 16, marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 10 },
  input: { background: 'var(--input-bg)', border: '1px solid var(--bdr)', borderRadius: 8, color: 'var(--txt)', padding: '9px 12px', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
  newAlbumBtns: { display: 'flex', gap: 8 },

  albumGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16, marginTop: 8 },
  albumCard:  { height: 160, borderRadius: 14, overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s', position: 'relative', border: '1px solid var(--bdr)' },
  albumOverlay: { position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1 },
  albumContent: { position: 'absolute', inset: 0, zIndex: 2, padding: '12px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' },
  albumCountBadge: { position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.45)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20, zIndex: 3 },
  albumName:      { margin: '0 0 2px', fontSize: 15, fontWeight: 700 },
  albumNameLight: { color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,0.5)' },
  albumNameDark:  { color: 'var(--txt)' },
  albumDesc:  { margin: 0, fontSize: 12, color: 'var(--txl)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' },
  albumDeleteBtn: { position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: 6, color: '#ef4444', fontSize: 16, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.15s', zIndex: 4 },

  dropzone: { border: '2px dashed', borderRadius: 12, padding: '28px 20px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s', marginBottom: 20 },
  dropText: { margin: '0 0 6px', fontSize: 15, color: 'var(--accent)', fontWeight: 500 },
  dropSub:  { margin: 0, fontSize: 12, color: 'var(--txl)' },
  progressList: { marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 4 },
  progressItem: { display: 'flex', justifyContent: 'space-between', background: 'var(--bg2)', borderRadius: 6, padding: '4px 10px', fontSize: 13 },
  progressName:   { color: 'var(--txm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' },
  progressStatus: { color: 'var(--txl)' },

  masonry:     { columns: '3 200px', columnGap: 12, marginTop: 8 },
  masonryItem: { breakInside: 'avoid', marginBottom: 12, borderRadius: 10, overflow: 'hidden', cursor: 'pointer', position: 'relative', background: 'var(--bg2)' },
  masonryImg:  { width: '100%', display: 'block', transition: 'transform 0.2s' },
  masonryOverlay: { position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)', padding: '20px 10px 8px', opacity: 0, transition: 'opacity 0.2s' },
  masonryCaption: { margin: '0 0 2px', fontSize: 12, color: '#fff', fontWeight: 500 },
  masonryMeta:    { margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.7)' },

  emptyState: { textAlign: 'center', padding: '60px 0' },
  emptyEmoji: { fontSize: 56, margin: '0 0 12px' },
  emptyText:  { color: 'var(--txl)', fontSize: 15, textAlign: 'center', padding: '20px 0' },

  lightboxOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  lightboxContent: { position: 'relative', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  lbImg:    { maxWidth: '90vw', maxHeight: '75vh', objectFit: 'contain', borderRadius: 8 },
  lbClose:  { position: 'absolute', top: -40, right: 0, background: 'none', border: 'none', color: '#fff', fontSize: 24, cursor: 'pointer', padding: '4px 8px' },
  lbArrow:  { position: 'absolute', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 36, cursor: 'pointer', borderRadius: 8, padding: '4px 10px', zIndex: 10 },
  lbMeta:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', width: '100%', marginTop: 12, gap: 16 },
  lbMetaLeft: { flex: 1 },
  lbCaption:  { margin: '0 0 4px', fontSize: 14, color: '#fff', fontWeight: 500 },
  lbInfo:     { margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.7)' },
  lbActions:  { display: 'flex', gap: 8 },
  lbBtn: { background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 18, width: 38, height: 38, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  lbCounter: { position: 'absolute', top: -40, left: 0, color: 'rgba(255,255,255,0.6)', fontSize: 13 },
};
